<template>
    <DashboardEditSection class="DriverNotificationsSection" title="Dispatch Notifications">
        <div class="row">
            <FormToggle :value="isInheriting" @input="handleToggleInherit"/>
            <div class="label">
                <h4>Use company default ({{ companyDefaultLabel }})</h4>
                <small>Turn this off to choose how this driver is notified.</small>
                <small v-if="isInheriting && missingContactFor.length" class="warning">
                    {{ missingContactFor.join(' ') }}
                </small>
            </div>
        </div>

        <div v-show="!isInheriting" class="override">
            <DispatchChannelToggles
                :value="channels"
                :email-available="!!email"
                :phone-available="!!phone"
                @input="handleChannelsChange"
            />

            <p v-if="$v.channels.$error" class="error">
                Choose at least one notification method, or use the company default.
            </p>
        </div>
    </DashboardEditSection>
</template>

<script>
import { mapState } from 'vuex';
import DashboardEditSection from '@/components/DashboardEditSection';
import DispatchChannelToggles from '@/components/form/DispatchChannelToggles.vue';
import FormToggle from '@/components/common/FormToggle.vue';

const SHORT_LABELS = { app: 'App', sms: 'Text', email: 'Email' };

export default {
    name: 'DriverNotificationsSection',

    components: { DashboardEditSection, DispatchChannelToggles, FormToggle },

    props: {
        // null means "inherit the company default", which is what the API stores for a
        // driver nobody has overridden.
        channels: { type: Array, default: null },
        email: { type: String, default: null },
        phone: { type: String, default: null },
    },

    computed: {
        ...mapState('company', ['dispatchNotificationChannels']),

        isInheriting() {
            return this.channels === null;
        },

        // Warn while inheriting too, not just while overriding: the channel toggles and
        // their notes are inside the override block, so a driver with no phone number
        // silently inheriting an SMS default would otherwise look correctly configured.
        //
        // Says what is missing rather than "cannot be notified" on purpose. Whether a
        // dispatch actually arrives depends on things this app cannot see -- SMS consent
        // and suppression, and whether the driver ever registered a device for push --
        // all of which live in the sidecar and are reported by its send preview. Having
        // an address on file is the most this screen can honestly speak to.
        missingContactFor() {
            const warnings = [];

            if (this.dispatchNotificationChannels.includes('sms') && !this.phone) {
                warnings.push('No phone number on file, so the Text default cannot reach them.');
            }

            if (this.dispatchNotificationChannels.includes('email') && !this.email) {
                warnings.push('No email address on file, so the Email default cannot reach them.');
            }

            return warnings;
        },

        companyDefaultLabel() {
            const channels = this.dispatchNotificationChannels;

            if (!channels.length) return 'dispatcher chooses each time';
            if (channels.includes('manual')) return 'never auto-notify';

            return channels.map(channel => SHORT_LABELS[channel]).join(', ');
        },
    },

    validations() {
        return {
            channels: {
                // Overriding with nothing selected is not a state the API can hold: it
                // normalises an empty override back to null, so the save would look like
                // it was ignored. Make the user choose instead.
                required: value => value === null || value.length > 0,
            },
        };
    },

    watch: {
        // A channel the driver has no address for cannot be honoured, so drop it if the
        // contact field is cleared while the form is open. Mirrors the way DriverForm
        // force-clears sendSetupEmail when the email is blanked.
        email(value) {
            if (!value) this.dropChannel('email');
        },

        phone(value) {
            if (!value) this.dropChannel('sms');
        },
    },

    methods: {
        validate() {
            this.$v.$touch();
            return !this.$v.$invalid;
        },

        dropChannel(channel) {
            if (!this.channels || !this.channels.includes(channel)) return;

            this.$emit('update:channels', this.channels.filter(value => value !== channel));
        },

        handleToggleInherit(isInheriting) {
            this.$analytics.trackClick(this.$options.name, 'use_company_dispatch_default', 'switch', {
                toggled_to: isInheriting,
            });

            if (isInheriting) {
                return this.$emit('update:channels', null);
            }

            // Seed the override from the company default so turning the toggle off is not
            // destructive, minus anything this driver has no address for.
            const seed = this.dispatchNotificationChannels.filter(channel => {
                if (channel === 'email') return !!this.email;
                if (channel === 'sms') return !!this.phone;

                return true;
            });

            this.$emit('update:channels', seed);
        },

        handleChannelsChange(channels) {
            this.$analytics.trackClick(this.$options.name, 'driver_dispatch_channels', 'switch', {
                channels: channels.join(','),
            });

            this.$emit('update:channels', channels);
        },
    },
};
</script>

<style lang="scss" scoped>
@import 'globals.scss';
.DriverNotificationsSection {
    .row {
        display: flex;
        align-items: center;

        h4 {
            margin: 0;
        }

        .FormToggle {
            margin-right: 15px;
            flex-shrink: 0;
        }

        small {
            display: block;
            color: $text-color-muted;
        }

        small.warning {
            color: $red-color;
        }
    }

    .override {
        margin-top: 25px;
    }

    .error {
        color: $red-color;
        margin: 10px 0 0;
    }
}
</style>
